import React from 'react';
import PropTypes from 'prop-types';

import backend from 'utils/backend';

import {Avatar, StickyTooltip, Menu, MenuItem} from 'views/components';
import {Translate, translate} from 'views/module';

import {userColours} from 'css/UserColours';

import {Person} from 'views/icons';

const AssigneeBadge = (props) => {
  const {
    interactive = true,
    user,
    onSelectAdmin,
    onUnassign,
    sx,
  } = props;

  const [userDetail, setUserDetail] = React.useState(user);
  const [admins, setAdmins] = React.useState([]);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);

  React.useEffect(() => {
    if (user?.firstName && user?.lastName) {
      setUserDetail(user);
      return;
    }

    if (!user?.userRn) {
      setUserDetail(user);
      return;
    }

    const fetchUser = async () => {
      try {
        const response = await backend.user.getUser(user.userRn, true);
        setUserDetail(response.user);
      } catch (error) {
        setUserDetail(user);
      }
    };

    fetchUser();
  }, [user]);

  React.useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const filter = [
          {key: 'status', value: ['ACTIVE']},
          {key: 'role', value: 'ADMIN'},
        ];
        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.user.queryUser(filter, sortBy);
        const uniqueOptions = Array.from(
            new Map((result?.list ?? []).map((admin) => [admin.userRn, admin])).values(),
        );

        setAdmins(uniqueOptions);
      } catch (error) {
        setAdmins([]);
      }
    };

    fetchAdmins();
  }, []);

  const getUserDisplayName = (user) => {
    return [user.firstName, user.lastName].filter(Boolean).join(' ');
  };

  const getUserDisplayInitials = (user) => {
    return [user.firstName?.charAt(0), user.lastName?.charAt(0)].filter(Boolean).join('').toUpperCase();
  };

  const handleClick = (event) => {
    if (!interactive) {
      return;
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectAdmin = (admin) => {
    onSelectAdmin(admin);
    handleClose();
  };

  const handleUnassign = () => {
    onUnassign();
    handleClose();
  };


  return (
    <>
      <StickyTooltip
        title={getUserDisplayName(userDetail) || translate('playbooks.New.StepPreview.Unassigned')}
      >
        <Avatar
          alt={getUserDisplayName(userDetail) || translate('playbooks.New.StepPreview.Unassigned')}
          component={interactive ? 'button' : 'div'}
          onClick={interactive ? handleClick : undefined}
          sx={{
            ...sx,
            overflow: 'hidden',
            backgroundColor: userDetail?.userRn ? userColours(userDetail.userRn) : 'transparent',
            border: userDetail?.userRn ? 'none' : '2px dashed #A8A49E',
            p: 0,
            cursor: interactive ? 'pointer' : 'default',
            color: userDetail?.userRn ? undefined : '#A8A49E',
          }}
        >
          {getUserDisplayInitials(userDetail) || <Person sx={{fontSize: 14}} />}
        </Avatar>
      </StickyTooltip>
      {
        interactive &&
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleClose}
        >
          {
            admins.map((admin) => (
              <MenuItem
                key={admin.userRn}
                onClick={() => handleSelectAdmin(admin)}
              >
                {getUserDisplayName(admin)}
              </MenuItem>
            ))
          }
          {
            userDetail?.userRn &&
            <MenuItem onClick={handleUnassign}>
              <Translate expression='playbooks.New.StepPreview.Unassign' />
            </MenuItem>
          }
        </Menu>
      }
    </>
  );
};

AssigneeBadge.propTypes = {
  interactive: PropTypes.bool,
  onSelectAdmin: PropTypes.func,
  onUnassign: PropTypes.func,
  user: PropTypes.shape({
    userRn: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }).isRequired,
  sx: PropTypes.object,
};

export default React.memo(AssigneeBadge);
